/**
 * Single source of truth for all language codes, display names, and mappings.
 *
 * Every component that needs language data should import from here
 * instead of defining its own local map.
 */

// $0.75 = 75 points at 1 point = $0.01. Cards below this threshold are "Bulk" tier
// and always use a 1.00x language multiplier — no annotation is shown to users.
export const BULK_TIER_POINTS_THRESHOLD = 75;

export interface Language {
  /** Frontend uppercase code: EN, ES, FR, DE, IT, PT, JA, KO, RU, ZHS, ZHT */
  code: string;
  /** Backend/Scryfall lowercase code: en, es, fr, de, it, pt, ja, ko, ru, zh-hans, zh-hant */
  backendCode: string;
  /** Full English display name */
  displayName: string;
  /** Short display abbreviation used in badges/pills */
  abbreviation: string;
}

/**
 * All 12 supported languages in display order.
 */
export const SUPPORTED_LANGUAGES: Language[] = [
  { code: 'EN',  backendCode: 'en',      displayName: 'English',              abbreviation: 'EN' },
  { code: 'ES',  backendCode: 'es',      displayName: 'Spanish',              abbreviation: 'ES' },
  { code: 'FR',  backendCode: 'fr',      displayName: 'French',               abbreviation: 'FR' },
  { code: 'DE',  backendCode: 'de',      displayName: 'German',               abbreviation: 'DE' },
  { code: 'IT',  backendCode: 'it',      displayName: 'Italian',              abbreviation: 'IT' },
  { code: 'PT',  backendCode: 'pt',      displayName: 'Portuguese',           abbreviation: 'PT' },
  { code: 'JA',  backendCode: 'ja',      displayName: 'Japanese',             abbreviation: 'JP' },
  { code: 'KO',  backendCode: 'ko',      displayName: 'Korean',               abbreviation: 'KR' },
  { code: 'RU',  backendCode: 'ru',      displayName: 'Russian',              abbreviation: 'RU' },
  { code: 'ZHS', backendCode: 'zh-hans', displayName: 'Chinese Simplified',   abbreviation: 'CN' },
  { code: 'ZHT', backendCode: 'zh-hant', displayName: 'Chinese Traditional',  abbreviation: 'TW' },
  { code: 'PH',  backendCode: 'ph',      displayName: 'Phyrexian',             abbreviation: 'PH' },
];

// ---------------------------------------------------------------------------
// Lookup maps (built once from SUPPORTED_LANGUAGES)
// ---------------------------------------------------------------------------

/** Map from any known code variant (case-insensitive) to its Language object */
const _lookup = new Map<string, Language>();
for (const lang of SUPPORTED_LANGUAGES) {
  // Index every variant we've seen in the codebase
  _lookup.set(lang.code.toLowerCase(), lang);              // en, es, ja, zhs, zht
  _lookup.set(lang.backendCode, lang);                     // en, es, ja, zh-hans, zh-hant
  _lookup.set(lang.displayName.toLowerCase(), lang);       // english, spanish, japanese
  _lookup.set(lang.abbreviation.toLowerCase(), lang);      // en, es, jp, kr, cn, tw
}
// Additional aliases encountered across the codebase
_lookup.set('s. chinese', SUPPORTED_LANGUAGES[9]);         // LanguageSelector display
_lookup.set('t. chinese', SUPPORTED_LANGUAGES[10]);        // LanguageSelector display
_lookup.set('chinese simplified', SUPPORTED_LANGUAGES[9]);
_lookup.set('chinese traditional', SUPPORTED_LANGUAGES[10]);
_lookup.set('chinese (s)', SUPPORTED_LANGUAGES[9]);
_lookup.set('chinese (t)', SUPPORTED_LANGUAGES[10]);
_lookup.set('simplified chinese', SUPPORTED_LANGUAGES[9]);
_lookup.set('traditional chinese', SUPPORTED_LANGUAGES[10]);
_lookup.set('chinese', SUPPORTED_LANGUAGES[9]);            // bare "chinese" → simplified
_lookup.set('zh', SUPPORTED_LANGUAGES[9]);                 // bare "zh" → simplified
_lookup.set('zhs', SUPPORTED_LANGUAGES[9]);
_lookup.set('zht', SUPPORTED_LANGUAGES[10]);

// Extra language codes that can appear in Scryfall data (not in our 12 supported,
// but we still want formatLanguage to produce a reasonable abbreviation)
const EXTRA_LANGUAGES: Record<string, string> = {
  'he': 'HE',  'hebrew': 'HE',
  'la': 'LA',  'latin': 'LA',
  'grc': 'GRC', 'ancient greek': 'GRC',
  'ar': 'AR',  'arabic': 'AR',
  'sa': 'SA',  'sanskrit': 'SA',

  'nl': 'NL',  'dutch': 'NL',
  'pl': 'PL',  'polish': 'PL',
  'cs': 'CS',  'czech': 'CS',
  'hu': 'HU',  'hungarian': 'HU',
};

// Extra display name lookup for non-standard languages (ViewCardPage uses these)
const EXTRA_DISPLAY_NAMES: Record<string, string> = {
  'he': 'Hebrew',
  'la': 'Latin',
  'grc': 'Ancient Greek',
  'ar': 'Arabic',
  'sa': 'Sanskrit',
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Normalize any language code/name variant to its short display abbreviation.
 * Returns the input uppercased (first 2 chars) if unrecognized.
 *
 * Examples:
 *   formatLanguage('ja')        → 'JP'
 *   formatLanguage('JAPANESE')  → 'JP'
 *   formatLanguage('zh-hans')   → 'CN'
 *   formatLanguage('Dutch')     → 'NL'
 */
export function formatLanguage(input: string): string {
  const key = input.toLowerCase();
  const lang = _lookup.get(key);
  if (lang) return lang.abbreviation;
  const extra = EXTRA_LANGUAGES[key];
  if (extra) return extra;
  return input.toUpperCase().slice(0, 2);
}

/**
 * Get the full display name for a language code.
 * Returns the input as-is if unrecognized.
 *
 * Examples:
 *   getLanguageDisplayName('ja')  → 'Japanese'
 *   getLanguageDisplayName('zhs') → 'Chinese Simplified'
 *   getLanguageDisplayName('he')  → 'Hebrew'
 */
export function getLanguageDisplayName(input: string): string {
  const key = input.toLowerCase();
  const lang = _lookup.get(key);
  if (lang) return lang.displayName;
  const extra = EXTRA_DISPLAY_NAMES[key];
  if (extra) return extra;
  return input;
}

/**
 * Convert an array of backend language codes to frontend uppercase codes.
 *
 * Example: ['en', 'ja', 'zh-hans'] → ['EN', 'JA', 'ZHS']
 */
export function mapBackendToFrontend(codes: string[]): string[] {
  if (!codes || codes.length === 0) return [];
  return codes.map(c => {
    const lang = _lookup.get(c.toLowerCase());
    return lang ? lang.code : c.toUpperCase();
  });
}

/**
 * Convert an array of frontend uppercase codes to backend codes.
 *
 * Example: ['EN', 'JA', 'ZHS'] → ['en', 'ja', 'zh-hans']
 */
export function mapFrontendToBackend(codes: string[]): string[] {
  if (!codes || codes.length === 0) return [];
  return codes.map(c => {
    const lang = _lookup.get(c.toLowerCase());
    return lang ? lang.backendCode : c.toLowerCase();
  });
}

/**
 * Format an array of language codes into a comma-separated display string.
 * Used in table cells to show accepted languages compactly.
 *
 * Example: ['en', 'ja'] → 'EN, JP'
 *          undefined     → 'EN'
 */
export function formatLanguages(languages?: string[]): string {
  if (!languages || languages.length === 0) return 'EN';
  return languages.map(formatLanguage).join(', ');
}

// ---------------------------------------------------------------------------
// Pre-built arrays for common UI patterns
// ---------------------------------------------------------------------------

/**
 * Dropdown options using backend codes as values and display names as labels.
 * Use for <select> elements that send values to the backend.
 *
 * Shape: { value: 'en', label: 'English' }
 */
export const LANGUAGE_OPTIONS = SUPPORTED_LANGUAGES.map(l => ({
  value: l.backendCode,
  label: l.displayName,
}));

/**
 * Dropdown options for search filters that include a "Default" (any language) option.
 */
export const SEARCH_LANGUAGE_OPTIONS = [
  { value: '', label: 'Default' },
  ...LANGUAGE_OPTIONS,
];

/**
 * Language pill options using backend codes and abbreviation labels.
 * Use for BatchEditActionBar-style pill selectors.
 *
 * Shape: { code: 'en', label: 'EN' }
 */
export const LANGUAGE_PILL_OPTIONS = SUPPORTED_LANGUAGES.map(l => ({
  code: l.backendCode,
  label: l.code,  // frontend uppercase code (EN, ES, JA, etc.)
}));

/**
 * Display names for LanguageSelector component.
 * Returns the array of display names in canonical order.
 */
export const LANGUAGE_DISPLAY_NAMES = SUPPORTED_LANGUAGES.map(l => l.displayName);

/**
 * Map from display name to frontend code, used by LanguageSelector.
 */
export const DISPLAY_NAME_TO_CODE: Record<string, string> = {};
for (const lang of SUPPORTED_LANGUAGES) {
  DISPLAY_NAME_TO_CODE[lang.displayName] = lang.code;
}

/**
 * Map from frontend code to display name, used by LanguageSelector.
 */
export const CODE_TO_DISPLAY_NAME: Record<string, string> = {};
for (const lang of SUPPORTED_LANGUAGES) {
  CODE_TO_DISPLAY_NAME[lang.code] = lang.displayName;
  // Also map lowercase backend codes
  CODE_TO_DISPLAY_NAME[lang.backendCode] = lang.displayName;
}

/**
 * Lookup map: any code variant → display abbreviation.
 * Superset of formatLanguage() as a plain object for components that need
 * a Record<string, string> instead of a function call.
 */
export const LANGUAGE_ABBREVIATION_MAP: Record<string, string> = {};
for (const lang of SUPPORTED_LANGUAGES) {
  LANGUAGE_ABBREVIATION_MAP[lang.code] = lang.abbreviation;
  LANGUAGE_ABBREVIATION_MAP[lang.code.toLowerCase()] = lang.abbreviation;
  LANGUAGE_ABBREVIATION_MAP[lang.backendCode] = lang.abbreviation;
  LANGUAGE_ABBREVIATION_MAP[lang.displayName] = lang.abbreviation;
  LANGUAGE_ABBREVIATION_MAP[lang.displayName.toLowerCase()] = lang.abbreviation;
}
// Add extra language abbreviations
for (const [key, value] of Object.entries(EXTRA_LANGUAGES)) {
  LANGUAGE_ABBREVIATION_MAP[key] = value;
  LANGUAGE_ABBREVIATION_MAP[key.toUpperCase()] = value;
}
// Chinese aliases
LANGUAGE_ABBREVIATION_MAP['S. Chinese'] = 'CN';
LANGUAGE_ABBREVIATION_MAP['T. Chinese'] = 'TW';
LANGUAGE_ABBREVIATION_MAP['Chinese Simplified'] = 'CN';
LANGUAGE_ABBREVIATION_MAP['Chinese Traditional'] = 'TW';
LANGUAGE_ABBREVIATION_MAP['Chinese (S)'] = 'CN';
LANGUAGE_ABBREVIATION_MAP['Chinese (T)'] = 'TW';
LANGUAGE_ABBREVIATION_MAP['Chinese'] = 'CN';
